import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { supabaseV2 } from "../../lib/supabase";
import { AuthShell } from "../../components/shell/AuthShell";

export function PasswordResetScreen({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation("auth");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(t("reset.errorTooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("reset.errorMismatch"));
      return;
    }
    setLoading(true);
    const { error: updErr } = await supabaseV2.auth.updateUser({ password });
    setLoading(false);
    if (updErr) {
      setError(updErr.message);
      return;
    }
    setDone(true);
    setTimeout(() => onDone(), 1200);
  };

  return (
    <AuthShell>
      <Card className="bg-transparent animate-fade-in">
        <CardHeader className="p-0 pb-5 space-y-2">
          <CardTitle className="text-[26px] font-bold leading-tight">{t("reset.title")}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {t("reset.subtitle")}
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="new-pwd">{t("reset.newPassword")}</Label>
              <Input
                id="new-pwd"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-pwd-confirm">{t("reset.confirmPassword")}</Label>
              <Input
                id="new-pwd-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                minLength={8}
                required
                autoComplete="new-password"
              />
            </div>
            {error && (
              <div className="rounded-lg px-3 py-2 text-sm bg-rose-50 text-rose-700 border border-rose-200">{error}</div>
            )}
            {done && (
              <div className="rounded-lg px-3 py-2 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200">
                {t("reset.done")}
              </div>
            )}
            <Button type="submit" variant="hero" className="w-full" disabled={loading || done}>
              {loading ? t("reset.submitting") : t("reset.submit")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthShell>
  );
}
